import { Button, Card, Checkbox, Divider, Form, Input } from "antd";
import { useForm } from "antd/es/form/Form";
import { pick } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { ROUTES } from "../../common/constants";
import { FloatingLabelField } from "../../components/FloatingLabelField";
import { Language } from "../../components/Language";
import Spinner from "../../components/Spinner";
import { loginStores } from "./LoginStores";
import { delLocalStorage, getLocalStorage, LOCAL_STORAGE_TYPE } from "../../common/localStorage";

const Login = observer(({ sso }) => {
  let history = useHistory();
  const [form] = useForm();
  const { search } = useLocation();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [resData, setResData] = useState();

  const signInClicked = async () => {
    const formData = await form.validateFields();
    setLoading(true);
    const isLoginSuccess = await loginStores.doLogin(formData);
    setLoading(false);
    window.AirflexJS.logEvent("login", pick(formData, ["email", "remember"]));
    if (isLoginSuccess) {
      history.push(getLocalStorage(LOCAL_STORAGE_TYPE.PRE_PATHNAME) || ROUTES.application);
      delLocalStorage(LOCAL_STORAGE_TYPE.PRE_PATHNAME);
    }
  };

  const code = useMemo(() => {
    const query = new URLSearchParams(search);
    return query.get("code");
  }, [search]);

  const confirmSignInADFS = () => {
    const isLoginSuccess = loginStores.initUserInfo(resData);
    window.AirflexJS.logEvent("login_via_adfs", { email: resData?.fullname });
    setResData(undefined);
    if (isLoginSuccess) {
      history.push(getLocalStorage(LOCAL_STORAGE_TYPE.PRE_PATHNAME) || ROUTES.application);
      delLocalStorage(LOCAL_STORAGE_TYPE.PRE_PATHNAME);
    };
  };

  const loginViaADFS = useCallback(async () => {
    if (sso && code) {
      const data = await loginStores.loginViaADFS(code);
      setResData(data);
    } else {
      setResData(undefined);
    }
  }, [code, sso]);

  useEffect(() => {
    loginViaADFS();
  }, [loginViaADFS]);

  if (sso && code && !resData) return <Spinner />;

  return (
    <div
      className="w-full h-screen bg-center bg-cover bg-no-repeat flex items-center justify-evenly"
      style={{ backgroundImage: "url('/assets/img/brand/background.svg')" }}
    >
      <div className="hidden lg:block">
        <img src="/assets/img/icons/computer.svg" alt="Computer Icon" width={533} height={537} />
      </div>
      <Card className="w-96 max-h-[92%] overflow-auto">
        <div className="flex justify-between items-center !mb-5">
          <img src="/assets/img/brand/logo.png" alt="logo" className="w-40" />
          {(!sso || !code) && <Language compact />}
        </div>
        <div className="w-full flex flex-1 flex-col justify-center">
          {sso && code ? (
            <div className="!mt-1">
              {resData?.token ? (
                <>
                  <div className="text-[18px] text-center">
                    <div>
                      {t("Hi")} <span className="text-main font-semibold">{resData?.fullname}</span>
                    </div>
                    <div>
                      {t("Continue signing in to")} <span className="text-main font-semibold">Airflex</span>
                    </div>
                  </div>
                  <Button
                    type="primary"
                    size="large"
                    className="w-full !mt-6 !mb-3 bg-gradient-primary"
                    onClick={confirmSignInADFS}
                  >
                    {t("Continue")}
                  </Button>
                </>
              ) : (
                <div className="text-[18px] text-center !mb-5">
                  <span>
                    {resData?.userInfo?.email || t("Your account")} {t("has not been granted access to")}
                  </span>{" "}
                  <span className="text-main font-semibold">Airflex</span>
                </div>
              )}
              <Button
                size="large"
                className="w-full text-main border-main"
                onClick={() => history.push("/login/vpbank")}
              >
                {t("Sign in with another account")}
              </Button>
            </div>
          ) : (
            <>
              <div className="text-2xl font-semibold">{t("Sign in")}</div>
              <div className="text-tgray !mt-2 !mb-5">{t("Please login to continue to your account")}</div>
              <Form form={form} layout="vertical" requiredMark={false} size="large">
                <Form.Item name="email" className="mb-2" rules={[{ required: true }]}>
                  <FloatingLabelField label={t("Email")}>
                    <Input />
                  </FloatingLabelField>
                </Form.Item>
                <Form.Item name="password" className="!mb-2" rules={[{ required: true }]}>
                  <FloatingLabelField label={t("Password")}>
                    <Input.Password />
                  </FloatingLabelField>
                </Form.Item>
                <Form.Item name="remember" className="!mb-2" valuePropName="checked">
                  <Checkbox>{t("Remember me")}</Checkbox>
                  <Link className="text-blue-500 float-right" to="/forgot-password">
                    {t("Forgot password?")}
                  </Link>
                </Form.Item>
                <Button
                  type="primary"
                  size="large"
                  className="w-full !mt-1 bg-gradient-primary"
                  onClick={signInClicked}
                  loading={loading}
                >
                  {t("Sign in")}
                </Button>
              </Form>
              {(sso || process.env.REACT_APP_ENVIRONMENT !== "PRODUCTION") && (
                <Divider className="!mt-5 !mb-3">{t("or")}</Divider>
              )}
              {sso && (
                <Button
                  size="large"
                  className="w-full text-main border-main mt-2 !mb-4"
                  onClick={() => window.open(process.env.REACT_APP_ADFS_LINK, "_self")}
                >
                  {t("Sign in with ADFS")}
                </Button>
              )}
              {process.env.REACT_APP_ENVIRONMENT !== "PRODUCTION" && (
                <div className="text-center">
                  {t("Don't have an account?")}&nbsp;
                  <a
                    href="/register"
                    className="text-blue-500"
                    onClick={(e) => {
                      e.preventDefault();
                      history.push("/register");
                    }}
                  >
                    {t("Sign up")}
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
});

export default Login;
